import type {
  FloorPlannerLayout,
  FloorPlannerObserver,
  RTSPEndpoint,
  StatementIssue,
  StatementObserver,
} from '@repo/frontend-types';
import {
  asArray,
  entityIdentityFor,
  findLatestDesign,
  floorEntityBid,
  floorTargetRefid,
  isLive,
  LIVE_ENTRY_MARKER,
} from '@repo/front-end-utils';

import { DEFAULT_TRACK_SIDE } from './observerSessionState';

/**
 * Put a running twin's live observers against the entities on its plan.
 *
 * A pure data transformation, deliberately: the whole matching rule is the part of this feature
 * most worth testing against real data, and it belongs somewhere a fixture taken off a real twin
 * can drive it with no React and no fetch. `matchObserversToPlan.test.ts` runs it against staging
 * twin `NP2-STN`'s actual 151 observers, 14 streams and 160 plan entities.
 *
 * ## The join, and why it works at all
 *
 * Both sides already carry the same value. An observer names its target with the full dGraph BID in
 * `twin_sense_observer.twin_entity_bid`; a plan surface or item carries the same value in
 * `entityCode` (ruling R18 — an entity's identity is its FULL BID, twin prefix and all). Folding
 * both through `entityIdentityFor` normalises case, whitespace and the un-migrated short form in one
 * step, and it is prefix-idempotent, so a plan that already stores the full BID is left alone.
 *
 * Measured on `NP2-STN`: 131 of 151 observers match on the identifier exactly, 19 more after the
 * ordinal suffix is stripped, and 1 does not match at all.
 *
 * ## The `.N` suffix
 *
 * Where an entity carries several observers they are distinguished by a dot and an ordinal —
 * `NP2-STN-A012.1`, `NP2-STN-A012.2` — and that string is the OBSERVER's identity, not an entity's.
 * There is no entity `NP2-STN-A012.2`, so the suffix is stripped before the lookup. It is stripped
 * from the folded identity rather than the raw value so that `np2-stn-a012.2` and `A012.2` both land
 * on the same key.
 *
 * ## Nothing is dropped in silence
 *
 * Three things can go wrong with a row and each is REPORTED rather than swallowed, because the
 * failure this whole feature exists to remove is a screen that renders an absence and a breakage
 * identically:
 *
 *   - the entity or floor it names is not on the plan → `unmatched`
 *   - its polygon is missing or unreadable    → `unreadableRegions`
 *   - its track side is not one this app has  → loaded as `TORSO`, and listed in `unrecognisedTrackSides`
 *
 * ## The region and the track side are READ ON THE SERVER now
 *
 * `readPolygon` and `readTrackSide` used to live here. They are the statement fold's job as of
 * `complex-measures-in-configuration-statement-read`: `polygon_config` and `cfg` are parsed once,
 * server-side, and this function is handed values that have already been read. **What it reports
 * has not changed** — the same three channels, filled from what the statement says.
 *
 * Two fields on the statement exist only so that stays true. `storedEntityBid` is the identifier
 * as the database holds it, suffix and all, because that is what this function has always named in
 * `unmatched`; and an `unrecognised-track-side` issue carries the offending raw `value`, because
 * the stage prints it back to the operator.
 */

/**
 * Marks an observer that came from the live configuration rather than being drawn in this session.
 * The value lives in `@repo/front-end-utils` (`LIVE_ENTRY_MARKER`) so the planner store can test it
 * too; this name is kept for the wizard's existing readers.
 */
export const LIVE_OBSERVER_MARKER = LIVE_ENTRY_MARKER;

export interface UnrecognisedTrackSide {
  observerId: number;
  entityBid: string;
  value: string;
}

export interface MatchObserversResult {
  /** Ready for `setAllFloorObservers` — one array per floor, indexed by floor. */
  observersByFloor: FloorPlannerObserver[][];
  /** Identifiers naming an entity this plan does not have. Stated to the operator, logged in full. */
  unmatched: string[];
  /**
   * Each `unmatched` identifier's observer NAME as stored, index-aligned, or `null` when blank
   * (`onboarding-polish-observer-notices`, R2). The notices' last-resort name; often the code itself.
   */
  unmatchedNames: (string | null)[];
  /** Rows whose `polygon_config` could not be read as a region. */
  unreadableRegions: string[];
  /** Rows carrying a track side this app cannot represent. Loaded as TORSO, and said so. */
  unrecognisedTrackSides: UnrecognisedTrackSide[];
  /** Placed successfully — the number the stage announces. */
  matched: number;
}

/** `NP2-STN-A012.2` → `NP2-STN-A012`. Only a trailing dot-and-digits, so `E014-old` is left alone. */
export const stripOrdinalSuffix = (identity: string): string => identity.replace(/\.\d+$/, '');

/**
 * Index the plan by entity identity, so a fold-and-look-up is one pass rather than a scan per
 * observer.
 *
 * Exported because `matchCompositesToPlan` must place a composite's target and inputs by exactly
 * the same rule an observer's target is placed by. Two implementations of "which plan entity is
 * this BID?" that drifted apart would put a composite on a different entity from the observers it
 * is meant to be adding up.
 */
export const indexPlanByIdentity = (
  layout: FloorPlannerLayout | null,
  twinBid: string | undefined,
): Map<string, { refid: string; kind: 'surface' | 'item'; floorIndex: number }> => {
  const index = new Map<string, { refid: string; kind: 'surface' | 'item'; floorIndex: number }>();
  if (!layout) return index;

  asArray(layout.floors).forEach((floor, floorIndex) => {
    const design = findLatestDesign(asArray(floor?.designs));
    const add = (refid: string, code: string | undefined, kind: 'surface' | 'item') => {
      const identity = entityIdentityFor(twinBid, code);
      // A blank identity names no entity; several plan items legitimately have none.
      // First writer wins, so a duplicated code cannot silently retarget an earlier entity.
      if (identity && !index.has(identity)) index.set(identity, { refid, kind, floorIndex });
    };
    asArray(design?.surfaces)
      .filter(isLive)
      .forEach(s => add(s.refid, s.entityCode, 'surface'));
    asArray(design?.items)
      .filter(isLive)
      .forEach(i => add(i.refid, i.entityCode, 'item'));
  });

  return index;
};

/**
 * Index the plan's FLOORS by identity — `{twinBid}-{floorName}`, folded exactly as an entity's is.
 *
 * ⛔ **Separate from `indexPlanByIdentity`, deliberately.** That index is exported and shared with
 * `matchCompositesToPlan`, which places a composite's floor target and floor inputs by floor NAME
 * through its own rule; adding floors to the shared index would change how composites are placed.
 * This one is consulted only after the entity index misses, so an entity can never be shadowed by
 * a floor of the same name.
 *
 * Why it exists at all (`refiling-preserves-configuration`, epic ledger row 121): a camera region can
 * count a whole floor. Staging PUL-CON held fifteen (`PUL-CON-Ground Floor.1` … `.12`,
 * `PUL-CON-First Floor.1` … `.3`), and with no floor to land on every one of them was reported
 * unmatched, dropped from the plan, and missing from the configuration Go Live filed.
 */
const indexPlanFloorsByIdentity = (
  layout: FloorPlannerLayout | null,
  twinBid: string | undefined,
): Map<string, { refid: string; kind: 'floor'; floorIndex: number }> => {
  const index = new Map<string, { refid: string; kind: 'floor'; floorIndex: number }>();
  if (!layout) return index;
  /*
   * ⚠ Two floors whose names fold the same are AMBIGUOUS, and neither is indexed — the same rule
   * `matchCompositesToPlan` applies to floor names. First-writer-wins would put the second floor's
   * cameras on the first floor's plan, against its cameras, silently; unindexed, they are reported
   * as unmatched instead, which the stage names.
   */
  const ambiguous = new Set<string>();
  asArray(layout.floors).forEach((floor, floorIndex) => {
    if (typeof floor?.id !== 'number') return;
    const identity = entityIdentityFor(twinBid, floorEntityBid(twinBid, floor.name));
    if (!identity) return;
    if (index.has(identity)) ambiguous.add(identity);
    else index.set(identity, { refid: floorTargetRefid(floor.id), kind: 'floor', floorIndex });
  });
  ambiguous.forEach(identity => index.delete(identity));
  return index;
};

export const matchObserversToPlan = (
  layout: FloorPlannerLayout | null,
  twinBid: string | undefined,
  observers: StatementObserver[],
  streams: RTSPEndpoint[],
  /**
   * The statement's issues. Only `unrecognised-track-side` is read: `trackSide: null` on its own
   * cannot tell an ABSENT track side (477 production rows, not an anomaly) from an unrecognised one
   * (reported, and its value shown). The issue is what separates them, and it carries the value.
   *
   * ⚠ **Required, deliberately, with no default.** This is now the ONLY route by which the stage can
   * still say "an observer is set to track something this app cannot represent" — the value used to
   * travel on the observer row and could not be dropped by a caller. As an optional parameter it
   * could: deleting the argument at the hook's call site disconnected the whole channel and left
   * all 844 Onboarding tests green, because neither the fixture nor the staging rig carries an
   * unrecognised track side. A required parameter makes that a compile error instead.
   */
  issues: StatementIssue[],
): MatchObserversResult => {
  const floorCount = asArray(layout?.floors).length;
  const observersByFloor: FloorPlannerObserver[][] = Array.from({ length: floorCount }, () => []);
  const unmatched: string[] = [];
  const unmatchedNames: (string | null)[] = [];
  const unreadableRegions: string[] = [];
  const unrecognisedTrackSides: UnrecognisedTrackSide[] = [];
  let matched = 0;

  const index = indexPlanByIdentity(layout, twinBid);
  const floorIndex = indexPlanFloorsByIdentity(layout, twinBid);
  const bidByEndpointId = new Map<number, string | null>(
    streams.map(s => [s.id, s.bid ?? null] as const),
  );
  const unrecognisedValueByObserverId = new Map<number, string>();
  for (const issue of issues) {
    if (
      issue.kind === 'unrecognised-track-side' &&
      issue.observerId != null &&
      issue.value != null
    ) {
      unrecognisedValueByObserverId.set(issue.observerId, issue.value);
    }
  }

  for (const observer of observers) {
    /*
     * ⚠ The ORDER of these three checks is the reporting rule, and it is unchanged.
     *
     * An observer naming an entity the plan does not have is reported ONLY as unmatched, even if its
     * region is also unreadable — the `continue` is what makes that true. An observer whose entity
     * IS on the plan but whose region cannot be read is reported as unreadable and not placed.
     * Reordering these quietly moves the counts the stage prints.
     */
    const folded = entityIdentityFor(twinBid, observer.storedEntityBid);
    const target =
      index.get(folded) ??
      index.get(stripOrdinalSuffix(folded)) ??
      floorIndex.get(folded) ??
      floorIndex.get(stripOrdinalSuffix(folded));
    if (!target) {
      // The identifier as STORED, suffix and all — this string is rendered to the operator.
      unmatched.push(observer.storedEntityBid);
      unmatchedNames.push(observer.name?.trim() || null);
      continue;
    }

    const points = observer.polygon;
    if (!points) {
      unreadableRegions.push(observer.storedEntityBid);
      continue;
    }

    // An absent track side is not an anomaly and is not reported; the app's default is exactly what
    // would have been written for it. An unrecognised one is a different thing, and the statement
    // raised an issue carrying the value.
    const trackSide = observer.trackSide ?? DEFAULT_TRACK_SIDE;
    const unrecognised = unrecognisedValueByObserverId.get(observer.id);
    if (unrecognised !== undefined) {
      unrecognisedTrackSides.push({
        observerId: observer.id,
        entityBid: observer.storedEntityBid,
        value: unrecognised,
      });
    }

    const endpointId = observer.rtspEndpointId ?? null;
    observersByFloor[target.floorIndex].push({
      /*
       * `{targetRefid}:live-{id}` — the observer's database id, so the refid is stable across
       * reloads, and distinct in shape from a drawn polygon's `{targetRefid}:{polygonId}` so a
       * re-match can tell which observers came from the live configuration and which the operator
       * drew this session. `LIVE_OBSERVER_MARKER` is the one definition of that distinction.
       */
      refid: `${target.refid}${LIVE_OBSERVER_MARKER}${observer.id}`,
      targetRefid: target.refid,
      targetKind: target.kind,
      rtspEndpointId: endpointId,
      rtspEndpointBid: endpointId != null ? (bidByEndpointId.get(endpointId) ?? null) : null,
      points,
      trackSide,
    });
    matched += 1;
  }

  return {
    observersByFloor,
    unmatched,
    unmatchedNames,
    unreadableRegions,
    unrecognisedTrackSides,
    matched,
  };
};
